import { z } from 'zod';
import {
  bigIntToNumberNullable,
  priceValidator,
  priceValidatorNullable,
  dateValidator,
  latitudeValidator,
  longitudeValidator,
  nonNegativeIntValidator,
  nonNegativeIntValidatorNullable,
  urlValidator,
} from '../common/validators';

/**
 * Listing photo schema
 * Contains photo URL for listing images
 */
export const ListingPhotoSchema = z.object({
  /** Photo URL */
  url: z.string(),
});

export type ListingPhoto = z.infer<typeof ListingPhotoSchema>;

/**
 * Listing tag schema
 * Represents a tag associated with a listing
 */
export const ListingTagSchema = z.object({
  /** Tag ID */
  id: nonNegativeIntValidator,
  /** Tag text */
  tag: z.string().max(100),
  /** Associated listing ID */
  listingId: nonNegativeIntValidator,
  /** Tag creation timestamp */
  createdDate: dateValidator,
});

export type ListingTag = z.infer<typeof ListingTagSchema>;

/**
 * Minimal listing schema for nested relations
 * Contains only essential fields for listing references
 */
export const ListingMinimalSchema = z.object({
  /** Listing ID */
  id: nonNegativeIntValidator,
  /** Listing title */
  title: z.string().max(254),
  /** Listing price (stored as string in DB) */
  price: z.string().max(25),
  /** Photo count (0 or more) */
  hasPhoto: z.number().int().min(0),
  /** Number of photos */
  photoCount: z.number().int().min(0).optional(),
});

export type ListingMinimal = z.infer<typeof ListingMinimalSchema>;

/**
 * Full listing schema with all fields
 * Represents a complete marketplace listing
 */
export const ListingSchema = z.object({
  /** Listing ID */
  id: nonNegativeIntValidator,
  /** User ID of listing owner */
  userId: bigIntToNumberNullable,
  /** Listing title */
  title: z.string().max(254),
  /** Listing description text */
  text: z.string().max(5000).nullable(),
  /** Listing price (stored as string) */
  price: z.string().max(25),
  /** Cost basis (for profit calculation) */
  cost: priceValidatorNullable,
  /** Original price before discount */
  original_price: priceValidatorNullable,
  /** Slashed price (deprecated field) */
  slashedPrice: priceValidatorNullable.optional(),
  /** Discount percentage */
  percentOff: z.number().min(0).max(100).nullable().optional(),
  /** Category ID */
  categoryId: z.number().int().positive(),
  /** Category group ID */
  categoryGroupId: z.number().int().min(0),
  /** Item condition (1-5 scale typically) */
  itemCondition: nonNegativeIntValidatorNullable,
  /** Listing status (active/inactive/sold) */
  status: z.number().int().min(0).max(255),
  /** Whether item is shippable */
  shippable: z.number().int().min(0).max(1),
  /** Location text (city, state) */
  locationText: z.string().max(125).nullable(),
  /** Listing creation timestamp */
  createdDate: dateValidator,
  /** Listing tags */
  tags: z.array(ListingTagSchema).optional(),
  /** Data source ID */
  dataSourceId: z.number().int().min(0),
  /** Remote URL (for imported listings) */
  remote_url: urlValidator,
  /** Item height in inches */
  height: nonNegativeIntValidatorNullable,
  /** Item width in inches */
  width: nonNegativeIntValidatorNullable,
  /** Item depth in inches */
  depth: nonNegativeIntValidatorNullable,
  /** Item weight in pounds */
  weight: priceValidatorNullable,
  /** Longitude coordinate */
  longitude: longitudeValidator,
  /** Latitude coordinate */
  latitude: latitudeValidator,
  /** Whether listing has photos */
  hasPhoto: z.number().int().min(0).max(1).optional(),
  /** Array of listing photos */
  photos: z.array(ListingPhotoSchema).optional(),
});

export type Listing = z.infer<typeof ListingSchema>;
